import { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import NotesIcon from "@mui/icons-material/Notes";
import SmsIcon from "@mui/icons-material/Sms";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { formatError } from "../../lib/errors";
import { useAuthStore } from "../../stores/auth";
import { ZonedDateTimeField } from "../../components/ZonedDateTimeField";
import { AssigneePicker } from "./AssigneePicker";

type LinkField = "person_id" | "organization_id" | "deal_id";

export interface ActivityTimelineProps {
  linkField: LinkField;
  linkId: string;
}

type ActivityType = "task" | "call" | "email" | "meeting" | "note" | "sms";

interface ActivityRow {
  id: string;
  type: ActivityType;
  subject: string;
  body: string | null;
  status: "open" | "completed" | "cancelled";
  due_at: string | null;
  completed_at: string | null;
  owner_user_id: string;
  owner_email: string | null;
  owner_name: string | null;
  created_at: string;
  person_id: string | null;
  organization_id: string | null;
  deal_id: string | null;
}

const TYPE_META: Record<ActivityType, { label: string; icon: typeof TaskAltIcon; color: string }> =
  {
    task: { label: "Task", icon: TaskAltIcon, color: "#7c4dff" },
    call: { label: "Call", icon: CallIcon, color: "#00897b" },
    email: { label: "Email", icon: EmailIcon, color: "#1e88e5" },
    meeting: { label: "Meeting", icon: EventIcon, color: "#ef6c00" },
    note: { label: "Note", icon: NotesIcon, color: "#607d8b" },
    sms: { label: "SMS", icon: SmsIcon, color: "#43a047" },
  };

function activityKey(linkField: LinkField, linkId: string) {
  return ["activities", linkField, linkId] as const;
}

function groupByDay(rows: ActivityRow[]): { day: string; items: ActivityRow[] }[] {
  const groups: Record<string, ActivityRow[]> = {};
  for (const r of rows) {
    const when = r.due_at ?? r.completed_at ?? r.created_at;
    const day = new Date(when).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    (groups[day] ??= []).push(r);
  }
  return Object.entries(groups).map(([day, items]) => ({ day, items }));
}

export function ActivityTimeline({ linkField, linkId }: ActivityTimelineProps) {
  const qc = useQueryClient();
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<ActivityRow | null>(null);

  const query = useQuery({
    queryKey: activityKey(linkField, linkId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manna_activities_view")
        .select("*")
        .eq(linkField, linkId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ActivityRow[];
    },
  });

  const complete = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("manna_complete_activity", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKey(linkField, linkId) }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("manna_soft_delete_activity", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKey(linkField, linkId) }),
  });

  const groups = groupByDay(query.data ?? []);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h3">Activity</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={composerOpen ? <CloseIcon /> : <AddIcon />}
            onClick={() => setComposerOpen((v) => !v)}
          >
            {composerOpen ? "Cancel" : "Log activity"}
          </Button>
        </Stack>

        {composerOpen && (
          <ActivityComposer
            linkField={linkField}
            linkId={linkId}
            onSaved={() => {
              setComposerOpen(false);
              qc.invalidateQueries({ queryKey: activityKey(linkField, linkId) });
            }}
          />
        )}

        {query.isLoading && <CircularProgress size={20} />}
        {query.error && <Alert severity="error">{formatError(query.error)}</Alert>}

        {!query.isLoading && (query.data ?? []).length === 0 && (
          <Typography variant="body2" color="text.secondary" py={2}>
            No activity yet. Log the first one above.
          </Typography>
        )}

        {groups.map((group) => (
          <Box key={group.day} mb={2}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {group.day}
            </Typography>
            <Stack spacing={1} mt={0.5}>
              {group.items.map((a) => {
                const meta = TYPE_META[a.type];
                const Icon = meta.icon;
                const done = a.status === "completed";
                return (
                  <Stack
                    key={a.id}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: meta.color,
                        width: 32,
                        height: 32,
                        mt: 0.25,
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            textDecoration: done ? "line-through" : "none",
                            color: done ? "text.secondary" : "text.primary",
                          }}
                        >
                          {a.subject}
                        </Typography>
                        <Chip label={meta.label} size="small" variant="outlined" />
                        {a.status === "cancelled" && (
                          <Chip label="Cancelled" size="small" color="default" />
                        )}
                        {a.due_at && !done && (
                          <Typography variant="caption" color="text.secondary">
                            Due {new Date(a.due_at).toLocaleString()}
                          </Typography>
                        )}
                        {done && a.completed_at && (
                          <Typography variant="caption" color="text.secondary">
                            Completed {new Date(a.completed_at).toLocaleString()}
                          </Typography>
                        )}
                      </Stack>
                      {a.body && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                        >
                          {a.body}
                        </Typography>
                      )}
                      {(a.owner_name ?? a.owner_email) && (
                        <Typography variant="caption" color="text.secondary">
                          {a.owner_name ?? a.owner_email}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setEditing(a)} aria-label="Edit">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!done && a.status !== "cancelled" && (
                        <Tooltip title="Mark complete">
                          <IconButton
                            size="small"
                            onClick={() => complete.mutate(a.id)}
                            disabled={complete.isPending}
                          >
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {done && (
                        <CheckCircleIcon fontSize="small" sx={{ color: "success.main", mt: 0.5 }} />
                      )}
                      <DeleteWithConfirm
                        onConfirm={() => remove.mutate(a.id)}
                        pending={remove.isPending}
                      />
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        ))}

        {editing && (
          <ActivityEditDialog
            activity={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              qc.invalidateQueries({ queryKey: activityKey(linkField, linkId) });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Edit dialog ──────────────────────────────────────────────────────────────

function ActivityEditDialog({
  activity,
  onClose,
  onSaved,
}: {
  activity: ActivityRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState<ActivityType>(activity.type);
  const [subject, setSubject] = useState(activity.subject ?? "");
  const [body, setBody] = useState(activity.body ?? "");
  // UTC ISO instant; ZonedDateTimeField manages the wall-clock + timezone entry.
  const [dueAtIso, setDueAtIso] = useState(activity.due_at ?? "");
  const [ownerUserId, setOwnerUserId] = useState<string | null>(activity.owner_user_id);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      // Status is left as-is here (the row's Complete/Cancel handle it); passing
      // the current status keeps completed_at stable.
      const { error } = await supabase.rpc("manna_update_activity", {
        p_id: activity.id,
        p_subject: subject.trim(),
        p_body: body.trim() || "",
        p_due_at: (dueAtIso || null) as unknown as string,
        p_status: activity.status,
        p_owner_user_id: ownerUserId ?? undefined,
        p_type: type,
      });
      if (error) throw error;
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit activity</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={0.5}>
          <TextField
            size="small"
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            autoFocus
          />
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value as ActivityType)}
              sx={{ width: 130, flexShrink: 0 }}
            >
              {(Object.keys(TYPE_META) as ActivityType[]).map((t) => (
                <MenuItem key={t} value={t}>
                  {TYPE_META[t].label}
                </MenuItem>
              ))}
            </TextField>
            <ZonedDateTimeField
              label="Due"
              value={dueAtIso || null}
              onChange={(v) => setDueAtIso(v ?? "")}
              sx={{ flex: 1 }}
            />
          </Stack>
          <AssigneePicker value={ownerUserId} onChange={setOwnerUserId} />
          <TextField
            size="small"
            label="Notes"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!subject.trim() || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────────────

function ActivityComposer({
  linkField,
  linkId,
  onSaved,
}: {
  linkField: LinkField;
  linkId: string;
  onSaved: () => void;
}) {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const [type, setType] = useState<ActivityType>("task");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // UTC ISO instant; ZonedDateTimeField manages the wall-clock + timezone entry.
  const [dueAtIso, setDueAtIso] = useState("");
  const [ownerUserId, setOwnerUserId] = useState<string | null>(currentUserId);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const args: Record<string, unknown> = {
        p_type: type,
        p_subject: subject.trim(),
        p_body: body.trim() || null,
        p_due_at: dueAtIso || null,
        p_owner_user_id: ownerUserId,
      };
      args[`p_${linkField}`] = linkId;
      const { error } = await supabase.rpc(
        "manna_create_activity",
        args as unknown as Parameters<typeof supabase.rpc<"manna_create_activity">>[1],
      );
      if (error) throw error;
    },
    onSuccess: () => {
      setSubject("");
      setBody("");
      setDueAtIso("");
      setOwnerUserId(currentUserId);
      setError(null);
      onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.default",
      }}
    >
      <Stack spacing={1}>
        <TextField
          size="small"
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          autoFocus
        />
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as ActivityType)}
            sx={{ width: 120, flexShrink: 0 }}
          >
            {(Object.keys(TYPE_META) as ActivityType[]).map((t) => (
              <MenuItem key={t} value={t}>
                {TYPE_META[t].label}
              </MenuItem>
            ))}
          </TextField>
          <ZonedDateTimeField
            label="Due"
            value={dueAtIso || null}
            onChange={(v) => setDueAtIso(v ?? "")}
            sx={{ flex: 1 }}
          />
        </Stack>
        <AssigneePicker value={ownerUserId} onChange={setOwnerUserId} />
        <TextField
          size="small"
          placeholder="Notes (optional)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            size="small"
            variant="contained"
            onClick={() => save.mutate()}
            disabled={!subject.trim() || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// Inline-confirm delete: clicking the trash icon opens a small Popper
// anchored to the button with Confirm + Cancel actions. Avoids the
// browser's native confirm() which positions at the top-left of the
// viewport and looks foreign in the rail.
function DeleteWithConfirm({ onConfirm, pending }: { onConfirm: () => void; pending: boolean }) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Delete">
        <IconButton
          ref={setAnchorEl}
          size="small"
          onClick={() => setOpen((v) => !v)}
          disabled={pending}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        sx={{ zIndex: (t) => t.zIndex.tooltip }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={120}>
            <Paper
              elevation={4}
              role="dialog"
              aria-label="Confirm delete activity"
              sx={{ p: 1.25, maxWidth: 220 }}
            >
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Delete this activity?
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => setOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setOpen(false);
                        onConfirm();
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
